<template>
  <div class="sources-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Источники оплаты"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/payment_sources/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <!-- Форма создания -->
      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card source-form-wrap">
          <h4 class="form-title">Создание источника оплаты</h4>
          <PaymentSourceForm
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="sources.length === 0" class="no-data">
        Нет источников оплаты
      </div>

      <ul v-else class="sources-list">
        <BaseSettingsListItemCard
          v-for="source in localSources"
          :key="source.id"
          :enabled="source.enabled"
          :is-editing="editingSourceId === source.id"
          :is-loading="isLoading"
          :created-at="source.created_at"
          :updated-at="source.updated_at"
          edit-title="Редактирование источника оплаты"
          @edit="startEdit(source)"
          @toggle="toggleSource(source)"
        >
          <template #edit>
            <PaymentSourceForm
              :initial-data="source"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(source.id, payload)"
              @cancel="editingSourceId = null"
            />
          </template>

          <div class="source-top">
            <span class="source-name">
              {{ source.name || "—" }}
            </span>
            <span v-if="source.bank_name" class="source-bank">
              {{ source.bank_name }}
            </span>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <!-- Пагинация -->
      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";

import { usePaymentSourceStore } from "@/stores/paymentSourceStore";
import { useNotificationStore } from "@/stores/notificationStore";

import PaymentSourceForm from "@/components/Expense/PaymentSourceForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "PaymentSourcesPage",
  components: {
    PaymentSourceForm,
    PaginationComponent,
  },
  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingSourceId: null,
      localSources: [],
    };
  },
  computed: {
    ...mapState(usePaymentSourceStore, [
      "sources",
      "isLoading",
      "pagination",
    ]),
    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadSources(newPage);
      },
    },
  },
  methods: {
    ...mapActions(usePaymentSourceStore, [
      "getSourcesRequest",
      "createSource",
      "updateSource",
      "sourceEnable",
      "sourceDisable",
    ]),

    async loadSources(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.getSourcesRequest({ page: pageNumber });
        this.localSources = [...this.sources];
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateLocalSource(updatedSource) {
      const target = this.localSources.find(
        (s) => s.id === updatedSource.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedSource);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingSourceId = null;
      }
    },

    startEdit(source) {
      this.editingSourceId = source.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createSource(payload);
        this.localSources = [...this.sources];
        this.showCreateForm = false;
        this.notificationStore.showSuccess("Источник оплаты создан");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateSource(id, payload);
        this.localSources = [...this.sources];
        this.editingSourceId = null;
        this.notificationStore.showSuccess(
          "Источник оплаты обновлён"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleSource(source) {
      try {
        if (source.enabled) {
          await this.sourceDisable(source.id);
          this.updateLocalSource({ ...source, enabled: false });
          this.notificationStore.showSuccess(
            "Источник оплаты отключён"
          );
        } else {
          await this.sourceEnable(source.id);
          this.updateLocalSource({ ...source, enabled: true });
          this.notificationStore.showSuccess(
            "Источник оплаты включён"
          );
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },
  async created() {
    await this.loadSources(this.$route.query.page);
  },
};
</script>

<style scoped>
.sources-container {
  flex-grow: 1;
}

.source-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.sources-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.source-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.source-name {
  font-weight: 600;
  font-size: 17px;
}

.source-bank {
  color: #666;
}
</style>
